import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { LinkTarget } from '../../typings/common';
import { CopyTooltipStatus } from '../../typings/domain';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ExpandableText } from '../ExpandableText/ExpandableText';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';
import { Skeleton } from '../Skeleton/Skeleton';
import { StyledLink } from '../StyledLink/StyledLink';
import { ScopedTooltip } from '../TooltipDecorator/ScopedTooltip';

import './DisplayConditions.css';
import * as keyset from './DisplayConditions.i18n';

const i18n = i18nFactory(keyset);
const BLOCK = 'display-conditions';
const RETARGETING_SHORT_VIEW_LENGTH = 4;

export enum KeywordsTextTypeView {
    vertical = 'vertical',
    horizontal = 'horizontal'
}

export interface DisplayConditionsProps {
    isOpen: boolean;
    isLoading: boolean;
    keywords?: string[];
    retargeting?: string[];
    editGroupLink: string;
    keywordsTextTypeView: KeywordsTextTypeView;
    onCopyClick: (text: string) => void;
    onChangeTextTypeView: (keywordsTextTypeView: KeywordsTextTypeView) => void;
    onControlClick: () => void;
    copyTooltipStatus: CopyTooltipStatus;
    cls?: string;
}

export class DisplayConditions extends React.PureComponent<
    DisplayConditionsProps
> {
    private copiedTooltipAnchor?: React.ReactNode;

    render() {
        const { isOpen, cls } = this.props;

        return (
            <div className={classNames(Bem.cls(BLOCK), cls)}>
                <div className={Bem.cls(BLOCK, 'header')}>
                    <div className={Bem.cls(BLOCK, 'title')}>
                        {i18n('title')}
                    </div>
                    {this.getControl()}
                </div>
                {isOpen ? this.getContent() : this.getDescription()}
            </div>
        );
    }

    private getControl = () => {
        const { isOpen, onControlClick } = this.props;

        return (
            <Shape2
                cls={Bem.cls({
                    block: BLOCK,
                    elem: 'control-element',
                    mods: {
                        iconType: Shape2Icon.verticalArrow,
                        opened: isOpen ? 'yes' : 'no'
                    }
                })}
                hoverable={true}
                theme={Shape2Theme.default}
                icon={Shape2Icon.verticalArrow}
                width={14}
                height={14}
                onClick={onControlClick}
            />
        );
    };

    private getDescription = () => {
        return (
            <div className={Bem.cls(BLOCK, 'description')}>
                {i18n('description')}
            </div>
        );
    };

    private getContent = () => {
        const { editGroupLink, isLoading } = this.props;

        return (
            <>
                {isLoading ? (
                    <div className={Bem.cls(BLOCK, 'skeleton')}>
                        {this.getSkeleton()}
                        {this.getSkeleton()}
                    </div>
                ) : (
                    <div className={Bem.cls(BLOCK, 'content-container')}>
                        {this.getKeywordsContent()}
                        {this.getRetargetingContent()}
                        <StyledLink
                            target={LinkTarget.blank}
                            href={editGroupLink}
                        >
                            <ButtonDecorator
                                cls={Bem.cls(BLOCK, 'go-to-edit-group-button')}
                                theme={'pseudo'}
                                text={i18n('go-to-edit-group')}
                                size={'n'}
                                tone="default"
                                view="default"
                            />
                        </StyledLink>
                    </div>
                )}
            </>
        );
    };

    private getKeywordsContent = () => {
        const { keywords } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'content')}>
                <div className={Bem.cls(BLOCK, 'keywords-header')}>
                    <div className={Bem.cls(BLOCK, 'keywords-title')}>
                        {i18n('keywords-title')}
                    </div>
                    {keywords && keywords.length > 0 && this.getIcons()}
                </div>
                <div className={Bem.cls(BLOCK, 'keywords-content')}>
                    {keywords && keywords.length > 0
                        ? this.getKeywords(keywords)
                        : i18n('no-items')}
                </div>
            </div>
        );
    };

    private getKeywords = (keywords: string[]) => {
        const { keywordsTextTypeView } = this.props;
        const separator =
            keywordsTextTypeView === KeywordsTextTypeView.vertical
                ? '\n'
                : ' · ';

        return keywords.join(separator);
    };

    private getIcons = () => {
        const { keywordsTextTypeView, copyTooltipStatus } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'keywords-icons')}>
                <Shape2
                    width={16}
                    height={16}
                    icon={Shape2Icon.shortText}
                    cls={Bem.cls({
                        block: BLOCK,
                        elem: 'icon',
                        mods: {
                            selected:
                                keywordsTextTypeView ===
                                KeywordsTextTypeView.vertical
                                    ? 'yes'
                                    : 'no'
                        }
                    })}
                    onClick={this.onVerticalIconClick}
                />
                <Shape2
                    width={16}
                    height={16}
                    icon={Shape2Icon.fullText}
                    cls={Bem.cls({
                        block: BLOCK,
                        elem: 'icon',
                        mods: {
                            selected:
                                keywordsTextTypeView ===
                                KeywordsTextTypeView.horizontal
                                    ? 'yes'
                                    : 'no'
                        }
                    })}
                    onClick={this.onHorizontalIconClick}
                />
                <Shape2
                    ref={ref => (this.copiedTooltipAnchor = ref)}
                    width={16}
                    height={16}
                    icon={Shape2Icon.copyNew}
                    cls={Bem.cls({
                        block: BLOCK,
                        elem: 'icon',
                        mods: {
                            type: 'copy'
                        }
                    })}
                    onClick={this.onCopyClick}
                />
                <ScopedTooltip
                    anchor={this.copiedTooltipAnchor}
                    theme={
                        copyTooltipStatus === CopyTooltipStatus.error
                            ? 'error'
                            : 'normal'
                    }
                    view={'classic'}
                    tone={'default'}
                    to={['right', 'left']}
                    size={'s'}
                    visible={copyTooltipStatus !== CopyTooltipStatus.hidden}
                >
                    {copyTooltipStatus === CopyTooltipStatus.error
                        ? i18n('copy-error')
                        : i18n('copied')}
                </ScopedTooltip>
            </div>
        );
    };

    private getRetargetingContent = () => {
        const { retargeting } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'content')}>
                <div className={Bem.cls(BLOCK, 'retargeting-title')}>
                    {i18n('retargeting-title')}
                </div>
                <div className={Bem.cls(BLOCK, 'retargeting-content')}>
                    {retargeting && retargeting.length > 0
                        ? this.getValueHint(retargeting)
                        : i18n('no-items')}
                </div>
            </div>
        );
    };

    private getValueHint = (retargeting: string[]) => {
        if (retargeting.length <= RETARGETING_SHORT_VIEW_LENGTH) {
            return this.getRetargetingList(retargeting);
        }

        return (
            <ExpandableText
                content={this.getRetargetingList(retargeting)}
                shortContent={this.getRetargetingList(
                    retargeting.slice(0, RETARGETING_SHORT_VIEW_LENGTH)
                )}
                openedStateSwitcherText={i18n('hide-full-value')}
                closedStateSwitcherText={i18n('show-full-value')}
            />
        );
    };

    private getRetargetingList = (items: string[]) => {
        return (
            <ol className={Bem.cls(BLOCK, 'value-list')}>
                {items.map(name => (
                    <li className={Bem.cls(BLOCK, 'value-list-item')}>
                        <span
                            className={Bem.cls({
                                block: BLOCK,
                                elem: 'value-list-item-text'
                            })}
                        >
                            {name}
                        </span>
                    </li>
                ))}
            </ol>
        );
    };

    private getSkeleton = () => {
        return (
            <div className={Bem.cls(BLOCK, 'content')}>
                <div
                    className={Bem.cls(BLOCK, 'skeleton', {
                        size: 'large'
                    })}
                >
                    <Skeleton isAnimating={true} height={14} />
                </div>
                <div
                    className={Bem.cls(BLOCK, 'skeleton', {
                        size: 'small'
                    })}
                >
                    <Skeleton isAnimating={true} height={14} />
                </div>
                <div
                    className={Bem.cls(BLOCK, 'skeleton', {
                        size: 'medium'
                    })}
                >
                    <Skeleton isAnimating={true} height={14} />
                </div>
            </div>
        );
    };

    private onHorizontalIconClick = () => {
        return this.props.onChangeTextTypeView(KeywordsTextTypeView.horizontal);
    };

    private onVerticalIconClick = () => {
        return this.props.onChangeTextTypeView(KeywordsTextTypeView.vertical);
    };

    private onCopyClick = () => {
        const { onCopyClick, keywords } = this.props;
        const text = keywords !== undefined ? keywords.join('\n') : '';

        return onCopyClick(text);
    };
}
